import { useFileUpload } from "@msuite/ui/file-upload";
import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { Panels } from "@msuite/ui/panels";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { type WorkspaceSchema, workspaceSchema } from "@msuite/utils/schemas";
import type { Tables } from "@msuite/utils/types";
import { FormProvider, useForm } from "react-hook-form";
import { useUpload } from "@/api/files";
import { LetterPaperPreview, WorkspaceForm } from "@/forms/workspace-form";
import { client } from "@/hono";
import { ModalLoadFailed } from "@/components/modals";

/** Props Interface */
interface EditWorkspaceModalProps extends ModalProps {
	workspaceId: string;
	onComplete?: () => void;
}

export function EditWorkspaceModal({
	isOpen,
	onClose,
	workspaceId,
	onComplete,
}: EditWorkspaceModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		const response = await client.organizations.workspaces[":workspaceId"].$get({
			param: {
				workspaceId,
			},
		});
		const data = await responseHandler(response as any);
		const workspace = data.workspace ?? ({} as Tables<"workspaces">);
		const workspaceMeta = data.workspaceMeta as any;
		return {
			...workspace,
			...workspaceMeta,
			region: workspaceMeta?.region ?? null,
			businessHours: workspaceMeta?.businessHours ?? null,
		} as WorkspaceSchema;
	});
	const methods = useForm({
		defaultValues,
		resolver: resolver(workspaceSchema),
	});
	const qc = useQueryClient();

	/** Uploaders */
	const { uploadFn } = useUpload();
	const workspaceLogoFileController = useFileUpload({
		uploadFn,
	});
	const workspaceLetterpaperFileController = useFileUpload({
		uploadFn,
	});

	/** Functions */
	async function handleOnSubmit(data: WorkspaceSchema) {
		try {
			const workspaceLetterpaperUploadResults =
				await workspaceLetterpaperFileController.handleOnFileUpload();
			const workspaceLogoUploadResults = await workspaceLogoFileController.handleOnFileUpload();
			await client.organizations.workspace[":workspaceId"].$patch({
				param: {
					workspaceId,
				},
				json: {
					...data,
					workspaceLetterpaper: {
						uploadResults: workspaceLetterpaperUploadResults,
					},
					workspaceLogo: {
						uploadResults: workspaceLogoUploadResults,
					},
				},
			});
			await qc.invalidateQueries({
				predicate: invalidate(["workspaces"]),
			});
			await onComplete?.();
			onClose();
		} catch (error) {
			console.error("Error updating workspace:", error);
		}
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={t("organization.edit_workspace")}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading && !loadFailed}
				width="70rem"
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<>
						<Modal.Body>
							<Panels>
								<Panels.Panel>
									<WorkspaceForm
										workspaceLogoFileController={workspaceLogoFileController}
										workspaceLetterpaperFileController={workspaceLetterpaperFileController}
										workspaceId={workspaceId}
									/>
								</Panels.Panel>
								<Panels.Panel flex={0}>
									<LetterPaperPreview
										workspaceLogoFileController={workspaceLogoFileController}
										workspaceLetterpaperFileController={workspaceLetterpaperFileController}
										workspaceId={workspaceId}
									/>
								</Panels.Panel>
							</Panels>
						</Modal.Body>
						<Modal.FooterForm />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
